import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSorter, numberSort } from "../../utils/functions.js";
import DataTable from "../../components/CustomDatatable/index.js";
import {
  Button,
  Card,
  Space,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Popconfirm,
  Switch,
  Modal,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Spinner } from "reactstrap";
import PackageForm from "./PackageForm.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import moment from "moment-timezone";
import {
  addPackage,
  deletePackage,
  getPackage,
  updatePackage,
  updatePackageStatus,
} from "../../store/actions/package.js";
import { getBusinessType } from "../../store/actions/businessType.js";

const { Title } = Typography;

const Package = () => {
  document.title = "Packages";
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const dispatch = useDispatch();

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const packageReducer = useSelector((state) => state.packageReducer.list);
  const businessType = useSelector((state) => state.businessType.list);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await dispatch(getPackage());
        await dispatch(getBusinessType());
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [dispatch]);

  const handleCreate = async (values) => {
    // const formData = new FormData();
    if (selectedRecord) {
      // formData.append("id", selectedRecord.id);
      const updateData = {
        id: selectedRecord.id,
        ...values,
        // Ensure product is always an array
        product: Array.isArray(values.product) ? values.product : [],
      };
      await dispatch(updatePackage(updateData));
      // await dispatch(updatePackage({ ...selectedRecord, ...values }));
      await dispatch(getPackage());
    } else {
      await dispatch(addPackage(values));
      await dispatch(getPackage());
    }
    setIsModalVisible(false);
  };

  // const handleDelete = async (id) => {
  //   await dispatch(deletePackage(id));
  //   await dispatch(getPackage());
  // };

  const handleDelete = async (item) => {
    setItemToDelete(item);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deletePackage(itemToDelete.id));
      await dispatch(getPackage());
      setDeleteModalVisible(false);
    } catch (error) {
      message.error("An error occurred while deleting the item");
    }
  };

  const handleChangeStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1; // Toggle status
    const updatedData = { id: id, status: newStatus }; // Create an object for update
    await dispatch(updatePackageStatus(updatedData));
    await dispatch(getPackage());
  };

  const renderCell = (text) => (text ? text : "N/A");
  let columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: stringSorter("name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sorter: stringSorter("description"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Gross Amt",
      dataIndex: "grossAmt",
      key: "grossAmt",
      sorter: numberSort("grossAmt"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Discount (%)",
      dataIndex: "discPer",
      key: "discPer",
      sorter: numberSort("discPer"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "amc Period",
      dataIndex: "amcPeriod",
      key: "amcPeriod",
      sorter: numberSort("amcPeriod"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Detail",
      dataIndex: "detail",
      key: "detail",
      sorter: stringSorter("detail"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Demo",
      dataIndex: "isDemo",
      key: "isDemo",
      sorter: (a, b) => (a.isDemo === b.isDemo ? 0 : a.isDemo ? -1 : 1),
      sortDirections: ["ascend", "descend"],
      render: (text) => (text ? "Yes" : "No"), // Converts boolean to "Yes" / "No"
    },
    {
      title: "Business Type",
      dataIndex: "businesstypename",
      key: "businesstypename",
      sorter: stringSorter("businesstypename"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        moment(date).isValid() ? moment(date).format("DD/MM/YYYY") : "",
      sorter: (a, b) =>
        moment(a.createdAt).isValid() && moment(b.createdAt).isValid()
          ? moment(a.createdAt).unix() - moment(b.createdAt).unix()
          : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      sorter: stringSorter("createdByName"),
      sortDirections: ["ascend", "descend"],
      render: renderCell,
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) =>
        moment(date).isValid() ? moment(date).format("DD/MM/YYYY") : "",
      sorter: (a, b) =>
        moment(a.updatedAt).isValid() && moment(b.updatedAt).isValid()
          ? moment(a.updatedAt).unix() - moment(b.updatedAt).unix()
          : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Updated By",
      dataIndex: "updatedByName",
      key: "updatedByName",
      sorter: stringSorter("updatedByName"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => {
              setSelectedRecord(record);
              setIsModalVisible(true);
            }}
          >
            <EditOutlined />
          </Button>
          {/* <Popconfirm
            title="Are you sure you want to delete this record?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm> */}
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            {/* Delete */}
          </Button>
        </Space>
      ),
    },
  ];
  columns = enhanceColumnsWithFilters(columns);

  return (
    <div className="page-content">
      <Breadcrumb style={{ marginBottom: "16px" }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Package</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                Manage Package
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedRecord(null);
                  setIsModalVisible(true);
                }}
              >
                Add Package
              </Button>
            </Col>
          </Row>
          {isLoading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <DataTable
              dataSource={packageReducer.map((item) => ({
                ...item,
                key: item.id,
                status: item.status ? 1 : 0,
              }))}
              columns={columns}
              isActive={true}
              onStatusChange={handleChangeStatus}
            />
          )}
        </Space>
      </Card>
      <PackageForm
        visible={isModalVisible}
        onCreate={handleCreate}
        onCancel={() => setIsModalVisible(false)}
        record={selectedRecord}
        businessType={businessType}
      />
      <Modal
        title="Confirm Delete"
        zIndex={100000000}
        visible={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setDeleteModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="delete" danger onClick={confirmDelete}>
            Delete
          </Button>,
        ]}
      >
        <p>Are you sure you want to delete this item?</p>
        {itemToDelete && (
          <p>
            <strong>Record Name:</strong> {itemToDelete.name}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default Package;
